// Admin Flow data access, platform-agnostic (ADR 0036). Every call here is Supabase and plain
// TypeScript, so the Admin Console can use it unchanged in a browser; the four functions that
// resize and upload images stayed behind in the mobile app, which is the only surface that
// captures them.
//
// The Supabase client is injected rather than imported: the mobile app builds one over
// AsyncStorage and the Console will build its own. See runtime.ts / setSharedRuntime().
import { getDictionary, normalizePinyin, supabase } from './runtime';
import { lookupWithFallback } from './dictionary';
import { pickTagColor } from './tagColors';
import type {
  Book,
  BookContributor,
  Contributor,
  ContributorRole,
  Spread,
  SpreadDraftPhrase,
  PhotoProcessing,
  ProcessingState,
  RecaptureRequest,
  Tag,
} from './admin';

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

function flattenPlacement(row: any): SpreadDraftPhrase {
  return {
    id: row.id,
    draft_phrase_id: row.draft_phrases.id,
    photo_id: row.photo_id,
    chinese: row.draft_phrases.chinese,
    pinyin: row.draft_phrases.pinyin,
    definition: row.draft_phrases.definition,
    assessment_level: row.draft_phrases.assessment_level,
    is_new: row.draft_phrases.is_new,
    vetted: row.draft_phrases.vetted,
    position: row.position,
    line_break_after: row.line_break_after,
    punctuation_after: row.punctuation_after ?? null,
    created_at: row.created_at,
    updated_at: row.updated_at,
    spread_id: row.spread_id,
  };
}

// The last Photo of a Spread (highest Photo position). For today's single-Photo pages this is
// simply the Spread's Photo; "append to the Spread" and per-Spread placement ops target it. ADR 0028.
async function getSpreadLastPhotoId(spreadId: string): Promise<string> {
  const { data, error } = await supabase
    .from('photos')
    .select('id')
    .eq('spread_id', spreadId)
    .order('position', { ascending: false })
    .limit(1);
  if (error) throw new Error(`getSpreadLastPhotoId: ${error.message}`);
  const id = data?.[0]?.id as string | undefined;
  if (!id) throw new Error(`getSpreadLastPhotoId: page ${spreadId} has no photos`);
  return id;
}

// Looks up a (chinese, pinyin) pair in the global Phrase Dictionary (phrases table).
// Pinyin must already be normalized. Returns the vetted definition + assessment_level, or null if absent.
async function lookupPhraseDictionary(
  chinese: string,
  normalizedPinyin: string,
): Promise<{ definition: string; assessment_level: number | null } | null> {
  const { data, error } = await supabase
    .from('phrases')
    .select('definition, assessment_level')
    .eq('chinese', chinese)
    .eq('pinyin', normalizedPinyin)
    .maybeSingle();
  if (error) throw new Error(`lookupPhraseDictionary: ${error.message}`);
  return data ?? null;
}

async function findOrCreateDraftPhrase(
  bookId: string,
  chinese: string,
  pinyin: string,
  definition: string | null,
  assessmentLevel: number | null,
  enrichFromDictionary = false,
): Promise<string> {
  const normalizedPinyin = await normalizePinyin(pinyin, chinese);
  const { data: existing, error: findError } = await supabase
    .from('draft_phrases')
    .select('id')
    .eq('book_id', bookId)
    .eq('chinese', chinese)
    .eq('pinyin', normalizedPinyin)
    .maybeSingle();
  if (findError) throw new Error(`findOrCreateDraftPhrase (find): ${findError.message}`);
  if (existing) return existing.id;

  // New draft phrase: determine whether it exists in the global Phrase Dictionary.
  const dictEntry = await lookupPhraseDictionary(chinese, normalizedPinyin);
  let finalDefinition = definition;
  let finalLevel = assessmentLevel;
  if (enrichFromDictionary && dictEntry) {
    finalDefinition = dictEntry.definition;
    finalLevel = dictEntry.assessment_level;
  }

  const { data: inserted, error: insertError } = await supabase
    .from('draft_phrases')
    .insert({
      book_id: bookId,
      chinese,
      pinyin: normalizedPinyin,
      definition: finalDefinition,
      assessment_level: finalLevel,
      is_new: dictEntry === null,
    })
    .select('id')
    .single();
  if (insertError) throw new Error(`findOrCreateDraftPhrase (insert): ${insertError.message}`);
  return inserted.id;
}

async function deleteOrphanedDraftPhrase(draftPhraseId: string): Promise<void> {
  const { count, error: countError } = await supabase
    .from('draft_photo_phrases')
    .select('id', { count: 'exact', head: true })
    .eq('draft_phrase_id', draftPhraseId);
  if (countError) throw new Error(`deleteOrphanedDraftPhrase (count): ${countError.message}`);
  if ((count ?? 1) === 0) {
    const { error } = await supabase.from('draft_phrases').delete().eq('id', draftPhraseId);
    if (error) throw new Error(`deleteOrphanedDraftPhrase (delete): ${error.message}`);
  }
}

// ---------------------------------------------------------------------------
// Books
// ---------------------------------------------------------------------------

export async function createBook(title: string): Promise<Book> {
  const { data, error } = await supabase
    .from('books')
    .insert({ title })
    .select()
    .single();

  if (error) throw new Error(`createBook: ${error.message}`);
  return data;
}

export async function getBookWithSpreads(
  bookId: string,
): Promise<Book & { spreads: Spread[] }> {
  const { data, error } = await supabase
    .from('books')
    .select('*, spreads(*)')
    .eq('id', bookId)
    .single();

  if (error) throw new Error(`getBookWithSpreads: ${error.message}`);
  // original_languages is NULL in the DB when unset; normalize to [] so the Book type
  // (string[]) holds and consumers never see null.
  return { ...data, original_languages: data.original_languages ?? [] };
}

export async function updateBookState(
  bookId: string,
  state: Book['state'],
): Promise<void> {
  const { error } = await supabase
    .from('books')
    .update({ state })
    .eq('id', bookId);

  if (error) throw new Error(`updateBookState: ${error.message}`);
}

// Publishes a book: promotes its draft phrases to the global Phrase Dictionary
// and flips book + pages to published. All of that lives in the publish_book RPC.
export async function publishBook(bookId: string): Promise<void> {
  const { error } = await supabase.rpc('publish_book', { p_book_id: bookId });
  if (error) throw new Error(`publishBook: ${error.message}`);
}

// Un-publishes a book, moving it back to In Review. Inverse of publishBook;
// the cleanup logic lives in the unpublish_book RPC.
export async function unpublishBook(bookId: string): Promise<void> {
  const { error } = await supabase.rpc('unpublish_book', { p_book_id: bookId });
  if (error) throw new Error(`unpublishBook: ${error.message}`);
}

// Updates a book's editable metadata. Pass only the fields you want to change;
// null clears a value.
export async function updateBookDetails(
  bookId: string,
  patch: { title?: string; description?: string | null; original_languages?: string[] | null; purchase_url?: string | null },
): Promise<void> {
  const { error } = await supabase
    .from('books')
    .update(patch)
    .eq('id', bookId);

  if (error) throw new Error(`updateBookDetails: ${error.message}`);
}

export async function getBookConsoleDetail(bookId: string): Promise<Book & { spreads: import('./admin').ConsoleSpread[]; tags: Tag[]; contributors: BookContributor[] }> {
  const [bookResult, tags, contributors] = await Promise.all([
    supabase.from('books').select('*, spreads(*, photos(id, photo_url, state, recapture_requested_at, recapture_note))').eq('id', bookId).single(),
    getBookTags(bookId),
    getBookContributors(bookId),
  ]);
  if (bookResult.error) throw new Error(`getBookConsoleDetail: ${bookResult.error.message}`);
  const spreads = ((bookResult.data.spreads ?? []) as any[]).map((spread) => {
    const photos = (spread.photos ?? []) as any[];
    const phraseCount = 0;
    return {
      ...spread,
      photos,
      phrase_count: phraseCount,
      unvetted_new_count: 0,
      missing_definition_count: 0,
    };
  });
  // Draft phrase state is joined separately because it is owned by Photos, not Spreads.
  const { data: placements, error: placementError } = await supabase
    .from('draft_photo_phrases')
    .select('spread_id, draft_phrases!inner(is_new, vetted, definition)')
    .in('spread_id', spreads.map((spread) => spread.id));
  if (placementError) throw new Error(`getBookConsoleDetail (phrases): ${placementError.message}`);
  const bySpread = new Map<string, any[]>();
  for (const row of (placements ?? []) as any[]) bySpread.set(row.spread_id, [...(bySpread.get(row.spread_id) ?? []), row.draft_phrases]);
  return {
    ...bookResult.data,
    original_languages: bookResult.data.original_languages ?? [],
    purchase_url: bookResult.data.purchase_url ?? null,
    tags,
    contributors,
    spreads: spreads.map((spread) => {
      const phrases = bySpread.get(spread.id) ?? [];
      return { ...spread, phrase_count: phrases.length, unvetted_new_count: phrases.filter((p) => p.is_new && !p.vetted).length, missing_definition_count: phrases.filter((p) => !p.definition?.trim()).length };
    }),
  };
}

export async function flagPhotoForRecapture(photoId: string, note: string): Promise<void> {
  const { error } = await supabase.from('photos').update({ recapture_requested_at: new Date().toISOString(), recapture_note: note.trim() || null }).eq('id', photoId);
  if (error) throw new Error(`flagPhotoForRecapture: ${error.message}`);
}

export async function getRecaptureRequests(): Promise<RecaptureRequest[]> {
  const { data, error } = await supabase
    .from('photos')
    .select('id, spread_id, position, photo_url, recapture_requested_at, recapture_note, spreads!inner(book_id, page_start, page_end, kind, books!inner(title))')
    .not('recapture_requested_at', 'is', null)
    .order('recapture_requested_at', { ascending: false });
  if (error) throw new Error(`getRecaptureRequests: ${error.message}`);

  return ((data ?? []) as any[]).map((row) => ({
    id: row.id,
    spread_id: row.spread_id,
    position: row.position,
    photo_url: row.photo_url,
    recapture_requested_at: row.recapture_requested_at,
    recapture_note: row.recapture_note,
    book_id: row.spreads.book_id,
    book_title: row.spreads.books.title,
    page_start: row.spreads.page_start,
    page_end: row.spreads.page_end,
    kind: row.spreads.kind,
  }));
}

export async function getRecaptureRequestCount(): Promise<number> {
  const { count, error } = await supabase
    .from('photos')
    .select('id', { count: 'exact', head: true })
    .not('recapture_requested_at', 'is', null);
  if (error) throw new Error(`getRecaptureRequestCount: ${error.message}`);
  return count ?? 0;
}

// The book_language enum labels, sourced from the DB so the UI never duplicates
// the value set. Declared order is preserved.
export async function getBookLanguages(): Promise<string[]> {
  const { data, error } = await supabase.rpc('book_language_values');
  if (error) throw new Error(`getBookLanguages: ${error.message}`);
  return (data as string[] | null) ?? [];
}

// ---------------------------------------------------------------------------
// Tags
// ---------------------------------------------------------------------------

export async function listTags(): Promise<Tag[]> {
  const { data, error } = await supabase
    .from('tags')
    .select('id, name, color')
    .order('name');
  if (error) throw new Error(`listTags: ${error.message}`);
  return data ?? [];
}

export async function getBookTags(bookId: string): Promise<Tag[]> {
  const { data, error } = await supabase
    .from('book_tags')
    .select('tags(id, name, color)')
    .eq('book_id', bookId);
  if (error) throw new Error(`getBookTags: ${error.message}`);
  return (data ?? []).map((row: any) => row.tags as Tag);
}

// Find-or-create a tag by its normalized (lowercase, trimmed) name. New tags get
// a spread-out palette color. Returns the existing or newly created tag.
export async function createTag(name: string): Promise<Tag> {
  const normalized = name.trim().toLowerCase();
  if (!normalized) throw new Error('createTag: name is empty');

  const { data: existing, error: findError } = await supabase
    .from('tags')
    .select('id, name, color')
    .eq('name', normalized)
    .maybeSingle();
  if (findError) throw new Error(`createTag (find): ${findError.message}`);
  if (existing) return existing;

  const all = await listTags();
  const color = pickTagColor(all.map((t) => t.color));

  const { data, error } = await supabase
    .from('tags')
    .insert({ name: normalized, color })
    .select('id, name, color')
    .single();
  if (error) throw new Error(`createTag: ${error.message}`);
  return data;
}

export async function assignTagToBook(bookId: string, tagId: string): Promise<void> {
  const { error } = await supabase
    .from('book_tags')
    .upsert({ book_id: bookId, tag_id: tagId }, { onConflict: 'book_id,tag_id' });
  if (error) throw new Error(`assignTagToBook: ${error.message}`);
}

export async function unassignTagFromBook(bookId: string, tagId: string): Promise<void> {
  const { error } = await supabase
    .from('book_tags')
    .delete()
    .eq('book_id', bookId)
    .eq('tag_id', tagId);
  if (error) throw new Error(`unassignTagFromBook: ${error.message}`);
}

// Rename a tag (propagates to every book carrying it). Normalizes the name and
// rejects a collision with a different existing tag.
export async function renameTag(tagId: string, name: string): Promise<Tag> {
  const normalized = name.trim().toLowerCase();
  if (!normalized) throw new Error('renameTag: name is empty');

  const { data: clash, error: checkError } = await supabase
    .from('tags')
    .select('id')
    .eq('name', normalized)
    .neq('id', tagId)
    .maybeSingle();
  if (checkError) throw new Error(`renameTag (check): ${checkError.message}`);
  if (clash) throw new Error(`A tag named "${normalized}" already exists.`);

  const { data, error } = await supabase
    .from('tags')
    .update({ name: normalized })
    .eq('id', tagId)
    .select('id, name, color')
    .single();
  if (error) throw new Error(`renameTag: ${error.message}`);
  return data;
}

export async function recolorTag(tagId: string, color: string): Promise<void> {
  const { error } = await supabase.from('tags').update({ color }).eq('id', tagId);
  if (error) throw new Error(`recolorTag: ${error.message}`);
}

// Delete a tag from the bank; cascades off every book via book_tags FK.
export async function deleteTag(tagId: string): Promise<void> {
  const { error } = await supabase.from('tags').delete().eq('id', tagId);
  if (error) throw new Error(`deleteTag: ${error.message}`);
}

// ---------------------------------------------------------------------------
// Contributors
// ---------------------------------------------------------------------------

export async function listContributors(): Promise<Contributor[]> {
  const { data, error } = await supabase
    .from('contributors')
    .select('id, name')
    .order('name');
  if (error) throw new Error(`listContributors: ${error.message}`);
  return data ?? [];
}

export async function getBookContributors(bookId: string): Promise<BookContributor[]> {
  const { data, error } = await supabase
    .from('book_contributors')
    .select('role, contributors(id, name)')
    .eq('book_id', bookId);
  if (error) throw new Error(`getBookContributors: ${error.message}`);
  return (data ?? []).map((row: any) => ({
    id: row.contributors.id,
    name: row.contributors.name,
    role: row.role as ContributorRole,
  }));
}

// Find-or-create a contributor by case-insensitive trimmed name, preserving the
// caller's display casing on creation. Returns the existing or new contributor.
export async function findOrCreateContributor(name: string): Promise<Contributor> {
  const trimmed = name.trim();
  if (!trimmed) throw new Error('findOrCreateContributor: name is empty');

  const { data: candidates, error: findError } = await supabase
    .from('contributors')
    .select('id, name')
    .ilike('name', trimmed);
  if (findError) throw new Error(`findOrCreateContributor (find): ${findError.message}`);
  const existing = (candidates ?? []).find(
    (c) => c.name.trim().toLowerCase() === trimmed.toLowerCase(),
  );
  if (existing) return existing;

  const { data, error } = await supabase
    .from('contributors')
    .insert({ name: trimmed })
    .select('id, name')
    .single();
  if (error) throw new Error(`findOrCreateContributor: ${error.message}`);
  return data;
}

export async function setBookContributor(
  bookId: string,
  contributorId: string,
  role: ContributorRole,
): Promise<void> {
  const { error } = await supabase
    .from('book_contributors')
    .upsert(
      { book_id: bookId, contributor_id: contributorId, role },
      { onConflict: 'book_id,contributor_id,role' },
    );
  if (error) throw new Error(`setBookContributor: ${error.message}`);
}

export async function removeBookContributor(
  bookId: string,
  contributorId: string,
  role: ContributorRole,
): Promise<void> {
  const { error } = await supabase
    .from('book_contributors')
    .delete()
    .eq('book_id', bookId)
    .eq('contributor_id', contributorId)
    .eq('role', role);
  if (error) throw new Error(`removeBookContributor: ${error.message}`);
}

// Rename a contributor (propagates to every book and role they're linked to).
// Preserves display casing; rejects a case-insensitive collision with another.
export async function renameContributor(
  contributorId: string,
  name: string,
): Promise<Contributor> {
  const trimmed = name.trim();
  if (!trimmed) throw new Error('renameContributor: name is empty');

  const { data: candidates, error: checkError } = await supabase
    .from('contributors')
    .select('id, name')
    .ilike('name', trimmed);
  if (checkError) throw new Error(`renameContributor (check): ${checkError.message}`);
  const clash = (candidates ?? []).find(
    (c) => c.id !== contributorId && c.name.trim().toLowerCase() === trimmed.toLowerCase(),
  );
  if (clash) throw new Error(`A contributor named "${trimmed}" already exists.`);

  const { data, error } = await supabase
    .from('contributors')
    .update({ name: trimmed })
    .eq('id', contributorId)
    .select('id, name')
    .single();
  if (error) throw new Error(`renameContributor: ${error.message}`);
  return data;
}

// Delete a contributor from the bank; cascades off every book via book_contributors FK.
export async function deleteContributor(contributorId: string): Promise<void> {
  const { error } = await supabase.from('contributors').delete().eq('id', contributorId);
  if (error) throw new Error(`deleteContributor: ${error.message}`);
}

// ---------------------------------------------------------------------------
// Pages
// ---------------------------------------------------------------------------

// Creates a content Spread. `pageNumber` doubles as the initial reading `position` — capture
// auto-sequences 1..N and an admin can later re-label numbers to match the physical book
// without disturbing order (ADR 0028).
export async function insertSpread(
  bookId: string,
  pageNumber: number,
  spreadId: string,
): Promise<Spread> {
  const { data, error } = await supabase
    .from('spreads')
    .insert({
      id: spreadId,
      book_id: bookId,
      page_start: pageNumber,
      page_end: pageNumber,
      position: pageNumber,
      kind: 'content',
    })
    .select()
    .single();

  if (error) throw new Error(`insertSpread: ${error.message}`);
  return data;
}

// Re-points books.cover_url at the Cover Spread's first Photo, when `photoId` *is* that Photo.
// A no-op for every other Photo, so callers can invoke it after an image change without first
// working out whether the Cover is involved.
export async function refreshCoverArtworkForPhoto(photoId: string): Promise<void> {
  const { data, error } = await supabase
    .from('photos')
    .select('spread_id, spreads!inner(book_id, kind)')
    .eq('id', photoId)
    .single();
  if (error) throw new Error(`refreshCoverArtworkForPhoto: ${error.message}`);

  const spread = (data as any).spreads as { book_id: string; kind: string };
  if (spread.kind !== 'cover') return;

  // The artwork comes from the Spread's first Photo carrying an image (same rule as
  // markSpreadAsCover), so replacing a later Photo of a multi-Photo Cover changes nothing.
  const photos = await getSpreadPhotos((data as any).spread_id as string);
  const coverPhoto = photos.find((p) => p.photo_url);
  if (!coverPhoto || coverPhoto.id !== photoId || !coverPhoto.photo_url) return;

  await setBookCoverArtwork(spread.book_id, coverPhoto.photo_url);
}

// ---------------------------------------------------------------------------
// Photos (ADR 0028) — a Spread is composed of one or more Photos; OCR is per-Photo.
// ---------------------------------------------------------------------------

// Create a Photo row under a Spread. `position` orders Photos within the Spread (a Spread's
// phrase list is its Photos' placements concatenated in this order). Fast DB op; the
// image is attached separately via uploadPhoto.
export async function insertPhoto(
  spreadId: string,
  position: number,
  photoId: string,
): Promise<void> {
  const { error } = await supabase
    .from('photos')
    .insert({ id: photoId, spread_id: spreadId, position });
  if (error) throw new Error(`insertPhoto: ${error.message}`);
}

// ---------------------------------------------------------------------------
// Review flow
// ---------------------------------------------------------------------------

export async function getBookWithSpreadCounts(
  bookId: string,
): Promise<Book & { spreads: Array<Spread & { draft_phrase_count: number }> }> {
  const { data, error } = await supabase
    .from('books')
    .select('*, spreads(*)')
    .eq('id', bookId)
    .single();

  if (error) throw new Error(`getBookWithSpreadCounts: ${error.message}`);

  const spreads: Spread[] = data.spreads ?? [];

  if (spreads.length === 0) {
    return { ...data, spreads: [] };
  }

  const spreadIds = spreads.map((p: Spread) => p.id);
  // Placements live on Photos now; count per Spread via the Photo→Spread join (ADR 0028).
  const { data: placements, error: placementsError } = await supabase
    .from('draft_photo_phrases')
// Naming the constraint is required, not stylistic. Migration 136 added a second foreign key
// from draft_photo_phrases to photos — the composite (photo_id, spread_id) that enforces
// Spread membership — so PostgREST can no longer guess which relationship an embed means and
// fails the whole request with PGRST201. Provenance is what these two want: photo_id.
    .select('id, photos!draft_photo_phrases_photo_id_fkey!inner(spread_id)')
    .in('photos.spread_id', spreadIds);

  if (placementsError) throw new Error(`getBookWithSpreadCounts: ${placementsError.message}`);

  const countMap: Record<string, number> = {};
  for (const row of (placements ?? []) as any[]) {
    const pid = row.photos.spread_id as string;
    countMap[pid] = (countMap[pid] ?? 0) + 1;
  }

  return {
    ...data,
    spreads: spreads.map((p: Spread) => ({
      ...p,
      draft_phrase_count: countMap[p.id] ?? 0,
    })),
  };
}

// Book + per-Photo OCR status for the Processing Status screen (ADR 0028). OCR is per
// Photo now; a Spread rolls up to in_review once all its Photos finish (02a). Ordered by
// page range then Photo position; the Cover (null page_start) sorts first.
export async function getBookProcessing(
  bookId: string,
): Promise<{ book: Book; photos: PhotoProcessing[] }> {
  const { data: book, error: bookError } = await supabase
    .from('books')
    .select('*')
    .eq('id', bookId)
    .single();
  if (bookError) throw new Error(`getBookProcessing (book): ${bookError.message}`);

  const { data, error } = await supabase
    .from('photos')
    .select('id, spread_id, position, state, error_message, photo_url, spreads!inner(page_start, book_id)')
    .eq('spreads.book_id', bookId);
  if (error) throw new Error(`getBookProcessing (photos): ${error.message}`);

  const photos: PhotoProcessing[] = (data ?? []).map((row: any) => ({
    id: row.id,
    spread_id: row.spread_id,
    page_number: (row.spreads as { page_start: number | null }).page_start,
    position: row.position,
    state: row.state,
    error_message: row.error_message,
    photo_url: row.photo_url,
  }));
  photos.sort(
    (a, b) => (a.page_number ?? -1) - (b.page_number ?? -1) || a.position - b.position,
  );
  return { book, photos };
}

// Retry a Photo by re-enqueuing it *unchanged*: the server-side worker (02a) claims it and
// re-dispatches to process-photo. Photo-level equivalent of resubmitPage. The right fix for a
// transient failure; for a misread, replacePhotoImage (a better crop) is what actually helps.
// Reached from ProcessingStatusScreen's retry and the Review Spread re-submit dialog.
export async function resubmitPhoto(photoId: string): Promise<void> {
  const { data: photo, error: photoError } = await supabase
    .from('photos')
    .select('spread_id')
    .eq('id', photoId)
    .single();
  if (photoError) throw new Error(`resubmitPhoto (photo): ${photoError.message}`);

  const { error } = await supabase
    .from('photos')
    .update({
      state: 'queued',
      attempts: 0,
      next_attempt_at: new Date().toISOString(),
      error_message: null,
    })
    .eq('id', photoId);
  if (error) throw new Error(`resubmitPhoto: ${error.message}`);

  const { error: confirmationError } = await supabase
    .from('spreads')
    .update({ confirmed_at: null })
    .eq('id', photo.spread_id);
  if (confirmationError) throw new Error(`resubmitPhoto (confirmation): ${confirmationError.message}`);
}

// The raw photos-row shape delivered by realtime UPDATE (no joined page_number).
export type PhotoProcessingChange = {
  id: string;
  spread_id: string;
  position: number;
  state: ProcessingState;
  error_message: string | null;
  photo_url: string | null;
};

// Observes processing progress for a book: invokes onPhotoChange / onBookChange whenever a
// Photo or the book changes, until the returned unsubscribe function is called. Photos carry
// no book_id, so we subscribe to all photo updates and let the caller filter by id (RLS
// already scopes these to admins). The realtime transport is an implementation detail.
export function subscribeToBookProcessing(
  bookId: string,
  handlers: {
    onPhotoChange: (photo: PhotoProcessingChange) => void;
    onBookChange: (book: Book) => void;
  },
): () => void {
  const channel = supabase
    .channel(`book-processing-${bookId}`)
    .on(
      'postgres_changes',
      { event: 'UPDATE', schema: 'public', table: 'photos' },
      (payload) => handlers.onPhotoChange(payload.new as PhotoProcessingChange),
    )
    .on(
      'postgres_changes',
      { event: 'UPDATE', schema: 'public', table: 'books', filter: `id=eq.${bookId}` },
      (payload) => handlers.onBookChange(payload.new as Book),
    )
    .subscribe();
  return () => {
    channel.unsubscribe();
  };
}

// The Photos composing a Spread, in order — reference images for the review screen (ADR 0028).
export async function getSpreadPhotos(
  spreadId: string,
): Promise<Array<{ id: string; position: number; photo_url: string | null }>> {
  const { data, error } = await supabase
    .from('photos')
    .select('id, position, photo_url')
    .eq('spread_id', spreadId)
    .order('position');
  if (error) throw new Error(`getSpreadPhotos: ${error.message}`);
  return data ?? [];
}

export async function getSpread(spreadId: string): Promise<Spread> {
  const { data, error } = await supabase
    .from('spreads')
    .select('*')
    .eq('id', spreadId)
    .single();

  if (error) throw new Error(`getSpread: ${error.message}`);
  return data;
}

// A Spread's phrase list is one ordered sequence — `position` is spread-scoped, so a phrase
// can sit anywhere regardless of which Photo produced it (ADR 0029, issue 08).
export async function getSpreadDraftPhrases(spreadId: string): Promise<SpreadDraftPhrase[]> {
  const { data, error } = await supabase
    .from('draft_photo_phrases')
    .select('*, draft_phrases(*)')
    .eq('spread_id', spreadId)
    .order('position');

  if (error) throw new Error(`getSpreadDraftPhrases: ${error.message}`);
  return (data ?? []).map(flattenPlacement);
}

export async function getBookDraftPhrases(bookId: string): Promise<SpreadDraftPhrase[]> {
  const { data, error } = await supabase
    .from('draft_photo_phrases')
    .select('*, draft_phrases(*), spreads!inner(book_id, position)')
    .eq('spreads.book_id', bookId)
    .order('position');
  if (error) throw new Error(`getBookDraftPhrases: ${error.message}`);
  return (data ?? []).sort((a: any, b: any) =>
    (a.spreads.position ?? 0) - (b.spreads.position ?? 0) || a.position - b.position,
  ).map(flattenPlacement);
}

export async function getDraftPhraseOccurrenceCount(draftPhraseId: string): Promise<number> {
  const { count, error } = await supabase
    .from('draft_photo_phrases')
    .select('id', { count: 'exact', head: true })
    .eq('draft_phrase_id', draftPhraseId);
  if (error) throw new Error(`getDraftPhraseOccurrenceCount: ${error.message}`);
  return count ?? 0;
}

// Updates phrase-level fields (definition, assessment_level) — propagates to all occurrences.
export async function updateDraftPhrase(
  draftPhraseId: string,
  fields: Partial<Pick<SpreadDraftPhrase, 'definition' | 'assessment_level'>>,
): Promise<void> {
  const { error } = await supabase
    .from('draft_phrases')
    .update(fields)
    .eq('id', draftPhraseId);

  if (error) throw new Error(`updateDraftPhrase: ${error.message}`);
}

export async function markDraftPhraseVetted(draftPhraseId: string): Promise<void> {
  const { error } = await supabase
    .from('draft_phrases')
    .update({ vetted: true })
    .eq('id', draftPhraseId);
  if (error) throw new Error(`markDraftPhraseVetted: ${error.message}`);
}

// Confirming is the Spread-level assertion: it first vets every remaining new Phrase on that
// Spread, then stamps confirmation. Vetting first prevents a failed vet write from leaving a
// Confirmed Spread carrying unvetted Phrases (ADR 0036).
export async function confirmSpread(spreadId: string): Promise<void> {
  const { data: placements, error: phraseError } = await supabase
    .from('draft_photo_phrases')
    .select('draft_phrase_id, draft_phrases!inner(is_new, vetted)')
    .eq('spread_id', spreadId);
  if (phraseError) throw new Error(`confirmSpread (phrases): ${phraseError.message}`);

  const ids = [...new Set((placements ?? [])
    .filter((row: any) => row.draft_phrases.is_new && !row.draft_phrases.vetted)
    .map((row: any) => row.draft_phrase_id as string))];
  if (ids.length > 0) {
    const { error } = await supabase.from('draft_phrases').update({ vetted: true }).in('id', ids);
    if (error) throw new Error(`confirmSpread (vet): ${error.message}`);
  }

  const { error: spreadError } = await supabase
    .from('spreads')
    .update({ confirmed_at: new Date().toISOString() })
    .eq('id', spreadId);
  if (spreadError) throw new Error(`confirmSpread (spread): ${spreadError.message}`);
}

// Changes pinyin on a draft_phrase. If (book_id, chinese, newPinyin) already exists,
// merges by rerouting all placements to that phrase and deleting the old one.
export async function changeDraftPhrasePinyin(
  draftPhraseId: string,
  newPinyin: string,
): Promise<void> {
  const { data: phrase, error: fetchError } = await supabase
    .from('draft_phrases')
    .select('book_id, chinese')
    .eq('id', draftPhraseId)
    .single();
  if (fetchError) throw new Error(`changeDraftPhrasePinyin (fetch): ${fetchError.message}`);

  const normalizedPinyin = await normalizePinyin(newPinyin, phrase.chinese);

  const { data: existing } = await supabase
    .from('draft_phrases')
    .select('id')
    .eq('book_id', phrase.book_id)
    .eq('chinese', phrase.chinese)
    .eq('pinyin', normalizedPinyin)
    .maybeSingle();

  if (existing) {
    const { error: rerouteError } = await supabase
      .from('draft_photo_phrases')
      .update({ draft_phrase_id: existing.id })
      .eq('draft_phrase_id', draftPhraseId);
    if (rerouteError) throw new Error(`changeDraftPhrasePinyin (reroute): ${rerouteError.message}`);
    const { error: deleteError } = await supabase
      .from('draft_phrases')
      .delete()
      .eq('id', draftPhraseId);
    if (deleteError) throw new Error(`changeDraftPhrasePinyin (delete): ${deleteError.message}`);
  } else {
    // New identity. The sync_draft_phrases trigger (BEFORE UPDATE OF pinyin) owns the
    // phrases-table lookup: it sets is_new, definition, assessment_level, and resets
    // vetted. We only handle what the trigger can't — the CEDICT fallback.
    const { data: updated, error } = await supabase
      .from('draft_phrases')
      .update({ pinyin: normalizedPinyin })
      .eq('id', draftPhraseId)
      .select('is_new')
      .single();
    if (error) throw new Error(`changeDraftPhrasePinyin (update): ${error.message}`);

    // Trigger found no phrases match → fill definition from bundled CEDICT.
    if (updated.is_new) {
      const dict = await getDictionary().catch(() => null);
      const cedictEntry = dict ? lookupWithFallback(dict, phrase.chinese) : null;
      if (cedictEntry) {
        const { error: defError } = await supabase
          .from('draft_phrases')
          .update({ definition: cedictEntry.definition })
          .eq('id', draftPhraseId);
        if (defError) throw new Error(`changeDraftPhrasePinyin (cedict): ${defError.message}`);
      }
    }
  }
}

// Updates placement-level fields (line_break_after) — per-occurrence only.
export async function updateDraftPlacement(
  placementId: string,
  fields: Partial<Pick<SpreadDraftPhrase, 'line_break_after'>>,
): Promise<void> {
  const { error } = await supabase
    .from('draft_photo_phrases')
    .update(fields)
    .eq('id', placementId);

  if (error) throw new Error(`updateDraftPlacement: ${error.message}`);
}

export interface SpreadDraftSnapshot {
  phrases: Array<Pick<SpreadDraftPhrase, 'chinese' | 'pinyin' | 'definition' | 'assessment_level' | 'is_new' | 'vetted'>>;
  placements: Array<Pick<SpreadDraftPhrase, 'photo_id' | 'chinese' | 'pinyin' | 'position' | 'line_break_after'> & { punctuation_after?: string | null }>;
}

// Restore is deliberately server-side: re-inserting an identity can invoke the Dictionary sync
// trigger, so replaying a snapshot in the browser could lose the Phrase's Vetted assertion.
export async function restoreSpreadDraft(spreadId: string, snapshot: SpreadDraftSnapshot): Promise<void> {
  const { error } = await supabase.rpc('restore_spread_draft', {
    p_spread_id: spreadId,
    p_snapshot: snapshot,
  });
  if (error) throw new Error(`restoreSpreadDraft: ${error.message}`);
}

// Changes the identity (Chinese text and/or pinyin) of a phrase at a SINGLE
// placement. Since (chinese, pinyin) is the identity key, this rewires only that
// one placement to a new (or existing) draft_phrase row, leaving other occurrences
// of the old phrase untouched, and cleans up the old row if it becomes orphaned.
export async function changeDraftPhraseIdentity(
  placementId: string,
  bookId: string,
  newChinese: string,
  newPinyin: string,
  newDefinition: string | null,
  newAssessmentLevel: number | null,
): Promise<{ draft_phrase_id: string }> {
  const { data: placement, error: fetchError } = await supabase
    .from('draft_photo_phrases')
    .select('draft_phrase_id')
    .eq('id', placementId)
    .single();
  if (fetchError) throw new Error(`changeDraftPhraseIdentity (fetch): ${fetchError.message}`);

  const oldDraftPhraseId = placement.draft_phrase_id;

  const newDraftPhraseId = await findOrCreateDraftPhrase(
    bookId, newChinese, newPinyin, newDefinition, newAssessmentLevel,
  );

  const { error: updateError } = await supabase
    .from('draft_photo_phrases')
    .update({ draft_phrase_id: newDraftPhraseId })
    .eq('id', placementId);
  if (updateError) throw new Error(`changeDraftPhraseIdentity (update): ${updateError.message}`);

  await deleteOrphanedDraftPhrase(oldDraftPhraseId);

  return { draft_phrase_id: newDraftPhraseId };
}

export async function deleteDraftPhrase(placementId: string): Promise<void> {
  const { data: placement, error: fetchError } = await supabase
    .from('draft_photo_phrases')
    .select('draft_phrase_id')
    .eq('id', placementId)
    .single();
  if (fetchError) throw new Error(`deleteDraftPhrase (fetch): ${fetchError.message}`);

  const { error: delError } = await supabase
    .from('draft_photo_phrases')
    .delete()
    .eq('id', placementId);
  if (delError) throw new Error(`deleteDraftPhrase (delete): ${delError.message}`);

  await deleteOrphanedDraftPhrase(placement.draft_phrase_id);
}

export type DraftPhraseReplacement = {
  draft_phrase_id: string;
  chinese: string;
  pinyin: string;
};

export async function replaceDraftPhraseCharacters(
  bookId: string,
  spreadId: string | null,
  replacements: DraftPhraseReplacement[],
): Promise<void> {
  if (replacements.length === 0) return;
  const { error } = await supabase.rpc('replace_draft_phrase_characters', {
    p_book_id: bookId,
    p_spread_id: spreadId,
    p_replacements: replacements,
  });
  if (error) throw new Error(`replaceDraftPhraseCharacters: ${error.message}`);
}

export type DraftBookSnapshot = {
  phrases: Array<Pick<SpreadDraftPhrase, 'chinese' | 'pinyin' | 'definition' | 'assessment_level' | 'is_new' | 'vetted'>>;
  placements: Array<Pick<SpreadDraftPhrase, 'photo_id' | 'id' | 'chinese' | 'pinyin' | 'position' | 'line_break_after'> & { spread_id: string; punctuation_after?: string | null }>;
};

export function makeBookDraftSnapshot(phrases: SpreadDraftPhrase[]): DraftBookSnapshot {
  return {
    phrases: [...new Map(phrases.map((phrase) => [phrase.chinese + '\u0000' + phrase.pinyin, phrase])).values()]
      .map(({ chinese, pinyin, definition, assessment_level, is_new, vetted }) => ({ chinese, pinyin, definition, assessment_level, is_new, vetted })),
    placements: phrases.map((phrase) => ({
      id: phrase.id, spread_id: (phrase as SpreadDraftPhrase & { spread_id?: string }).spread_id ?? '',
      photo_id: phrase.photo_id, chinese: phrase.chinese, pinyin: phrase.pinyin,
      position: phrase.position, line_break_after: phrase.line_break_after,
      punctuation_after: phrase.punctuation_after ?? null,
    })),
  };
}

export async function restoreBookDraft(snapshot: DraftBookSnapshot, bookId: string): Promise<void> {
  const { error } = await supabase.rpc('restore_book_draft', { p_book_id: bookId, p_snapshot: snapshot });
  if (error) throw new Error(`restoreBookDraft: ${error.message}`);
}

export async function insertDraftPhrase(
  spreadId: string,
  chinese: string,
  pinyin: string,
  definition: string | null = null,
  assessmentLevel: number | null = null,
): Promise<SpreadDraftPhrase> {
  const { data: pageRow, error: pageError } = await supabase
    .from('spreads')
    .select('book_id')
    .eq('id', spreadId)
    .single();
  if (pageError) throw new Error(`insertDraftPhrase (spread): ${pageError.message}`);

  const draftPhraseId = await findOrCreateDraftPhrase(
    pageRow.book_id, chinese, pinyin, definition, assessmentLevel, true,
  );

  // Append to the end of the Spread's sequence. Provenance is the Spread's last Photo, so a
  // hand-added phrase is replaced only if that Photo is re-read (issue 08).
  const photoId = await getSpreadLastPhotoId(spreadId);

  const { data: existing } = await supabase
    .from('draft_photo_phrases')
    .select('position')
    .eq('spread_id', spreadId)
    .order('position', { ascending: false })
    .limit(1);

  const position = existing && existing.length > 0 ? existing[0].position + 1 : 0;

  const { data, error } = await supabase
    .from('draft_photo_phrases')
    .insert({ photo_id: photoId, spread_id: spreadId, draft_phrase_id: draftPhraseId, position, line_break_after: false })
    .select('*, draft_phrases(*)')
    .single();
  if (error) throw new Error(`insertDraftPhrase (placement): ${error.message}`);
  return flattenPlacement(data);
}

export async function getAllBooks(): Promise<Array<Book & { page_count: number }>> {
  const { data, error } = await supabase
    .from('books')
    // The Book's physical page count is books.total_pages — the figure an admin maintains in
    // review. It can't be derived from the Spreads: pages without text are never photographed,
    // and one Spread may cover several pages (ADR 0029). Content Spreads are only a fallback
    // for books that predate the field.
    .select('id, title, state, cover_url, description, original_languages, created_at, updated_at, total_pages, spreads(id, kind)')
    .in('state', ['in_review', 'published'])
    .order('updated_at', { ascending: false });

  if (error) throw new Error(`getAllBooks: ${error.message}`);

  return (data ?? []).map((book: any) => ({
    id: book.id,
    title: book.title,
    state: book.state,
    cover_url: book.cover_url,
    purchase_url: book.purchase_url ?? null,
    description: book.description,
    original_languages: book.original_languages ?? [],
    created_at: book.created_at,
    updated_at: book.updated_at,
    page_count:
      book.total_pages ?? (book.spreads ?? []).filter((p: any) => p.kind !== 'cover').length,
  }));
}

// Public URL for a Spread Landmark (ADR 0032). Mirrors readingApi's copy — the admin and reading
// layers deliberately don't import each other, the same way getBookCoverUrl exists in both.
export function getSpreadLandmarkUrl(landmarkPath: string): string {
  const { data } = supabase.storage.from('spread-landmarks').getPublicUrl(landmarkPath);
  return data.publicUrl;
}

// Unlike readingApi's copy, this defaults to the *untransformed* original: the admin surfaces
// that show a cover are reviewing it (ReviewSpread draws it as a full-size review photo, where
// downscaling would hide the very detail the review is for). Callers rendering a thumbnail pass
// a width and get the transformed variant — see the note on readingApi.getBookCoverUrl.
export function getBookCoverUrl(coverPath: string, width?: number): string {
  const { data } = supabase.storage
    .from('book-covers')
    .getPublicUrl(coverPath, width ? { transform: { width, resize: 'contain', quality: 75 } } : undefined);
  return data.publicUrl;
}

export async function getPagePhotoUrl(storagePath: string): Promise<string> {
  const { data, error } = await supabase.storage
    .from('page-photos')
    .createSignedUrl(storagePath, 3600);

  if (error) throw new Error(`getPagePhotoUrl: ${error.message}`);
  return data.signedUrl;
}

export async function deleteBook(bookId: string): Promise<void> {
  const { error } = await supabase.from('books').delete().eq('id', bookId);
  if (error) throw new Error(`deleteBook: ${error.message}`);
}

export async function deleteSpread(spreadId: string): Promise<void> {
  // Collect draft_phrase_ids before cascade deletes them with the page. Placements live on
  // the page's Photos now (ADR 0028), reached via the Photo→Spread join.
  const { data: placements, error: fetchError } = await supabase
    .from('draft_photo_phrases')
    .select('draft_phrase_id, photos!draft_photo_phrases_photo_id_fkey!inner(spread_id)')
    .eq('photos.spread_id', spreadId);
  if (fetchError) throw new Error(`deleteSpread (fetch): ${fetchError.message}`);

  const draftPhraseIds = [
    ...new Set((placements ?? []).map((p: { draft_phrase_id: string }) => p.draft_phrase_id)),
  ];

  // Delete the page — cascades to photos and their draft_photo_phrases.
  const { error } = await supabase.from('spreads').delete().eq('id', spreadId);
  if (error) throw new Error(`deleteSpread: ${error.message}`);

  // Clean up any draft_phrases now orphaned by the cascade.
  for (const draftPhraseId of draftPhraseIds) {
    await deleteOrphanedDraftPhrase(draftPhraseId);
  }
}

// The Book's Spreads in reading order (a `pages` row is a Spread — ADR 0029). A Spread covers
// the physical page range page_start..page_end; both are null for the Cover.
export async function getBookSpreadList(
  bookId: string,
): Promise<
  Array<{
    id: string;
    page_start: number | null;
    page_end: number | null;
    position: number | null;
    kind: 'content' | 'cover';
    state: string;
    landmark_url: string | null;
  }>
> {
  const { data, error } = await supabase
    .from('spreads')
    .select('id, page_start, page_end, position, kind, state, landmark_url')
    .eq('book_id', bookId)
    .order('position', { nullsFirst: false })
    .order('page_start', { nullsFirst: true });

  if (error) throw new Error(`getBookSpreadList: ${error.message}`);
  return (data ?? []) as any;
}

// Sets the physical page range a Spread covers, so the app's numbering matches the book in
// the Reader's hands. Ranges may be sparse (un-photographed pages) — reading order comes from
// `position`, not these numbers. Works on photoless Spreads (ADR 0029).
// Later Spreads are pushed forward if the new range would overlap them, so widening or moving
// a range never fails on a collision (the RPC owns that cascade).
export async function updateSpreadPageRange(
  spreadId: string,
  pageStart: number | null,
  pageEnd: number | null,
): Promise<void> {
  const { error } = await supabase.rpc('set_spread_page_range', {
    p_spread_id: spreadId,
    p_start: pageStart,
    p_end: pageEnd ?? pageStart,
  });
  if (error) throw new Error(`updateSpreadPageRange: ${error.message}`);
}

// The Book's total physical page count — it can't be derived, since pages without text are
// never photographed (ADR 0029).
export async function updateBookTotalPages(
  bookId: string,
  totalPages: number | null,
): Promise<void> {
  const { error } = await supabase
    .from('books')
    .update({ total_pages: totalPages })
    .eq('id', bookId);
  if (error) throw new Error(`updateBookTotalPages: ${error.message}`);
}

export async function getBookTotalPages(bookId: string): Promise<number | null> {
  const { data, error } = await supabase
    .from('books')
    .select('total_pages')
    .eq('id', bookId)
    .single();
  if (error) throw new Error(`getBookTotalPages: ${error.message}`);
  return (data?.total_pages as number | null) ?? null;
}

// The Book's cover artwork as a book-covers storage path (getBookCoverUrl turns it into a URL).
// Outlives the Photos it was copied from — publish_book discards a Book's Photos — which is what
// lets the Review flow still show a Cover image after a Book is unpublished.
export async function getBookCoverPath(bookId: string): Promise<string | null> {
  const { data, error } = await supabase
    .from('books')
    .select('cover_url')
    .eq('id', bookId)
    .single();
  if (error) throw new Error(`getBookCoverPath: ${error.message}`);
  return (data?.cover_url as string | null) ?? null;
}

// Detaches a Photo into its own Spread, immediately after its current one — the undo for a
// mis-merge, now that grouping is physical (ADR 0029). Returns the new Spread's id.
export async function splitPhotoToNewSpread(photoId: string): Promise<string> {
  const { data, error } = await supabase.rpc('split_photo_to_new_spread', {
    p_photo_id: photoId,
  });
  if (error) throw new Error(`splitPhotoToNewSpread: ${error.message}`);
  return data as string;
}

// Removes a Spread's Landmark: the Navigator box falls back to its page label, which is the
// ordinary presentation rather than a degraded one. Drops the storage object too — orphaning is
// the established sin in this codebase (publish leaves Photo objects behind) and not worth
// repeating in a public bucket.
export async function clearSpreadLandmark(spreadId: string): Promise<void> {
  const { data: existing } = await supabase
    .from('spreads')
    .select('landmark_url')
    .eq('id', spreadId)
    .single();
  const previousPath = existing?.landmark_url as string | null | undefined;

  const { error } = await supabase
    .from('spreads')
    .update({ landmark_url: null })
    .eq('id', spreadId);
  if (error) throw new Error(`clearSpreadLandmark: ${error.message}`);

  if (previousPath) {
    await supabase.storage.from('spread-landmarks').remove([previousPath]);
  }
}

// Opens `count` positions immediately after a Spread and creates that many new content Spreads
// there, returning their ids in position order (migration …122). How an admin adds pages missed
// during capture. Structural only: the caller attaches one Photo per returned Spread, and it is
// setting photos.photo_url that starts OCR — this call starts nothing. Non-destructive, so
// unlike deleteSpread there is no orphaned draft_phrase or storage cleanup to do afterwards.
export async function insertSpreadsAfter(spreadId: string, count: number): Promise<string[]> {
  const { data, error } = await supabase.rpc('insert_spreads_after', {
    p_spread_id: spreadId,
    p_count: count,
  });
  if (error) throw new Error(`insertSpreadsAfter: ${error.message}`);
  return (data ?? []) as string[];
}

// Marks a Spread as the Book's Cover (ADR 0028): it becomes kind='cover', loses its page number
// (the Cover is unnumbered and excluded from the page total), and its first Photo's image is
// copied to the book-covers bucket to back books.cover_url. Any previous Cover reverts to a
// content Spread. Sets cover_url directly rather than re-OCRing, since process-photo only seeds
// it at OCR time. A Spread with no Photo still becomes the Cover — just without new artwork.
export async function markSpreadAsCover(bookId: string, spreadId: string): Promise<void> {
  // Demote an existing Cover first; it keeps its (null) number for the admin to set.
  const { error: demoteError } = await supabase
    .from('spreads')
    .update({ kind: 'content' })
    .eq('book_id', bookId)
    .eq('kind', 'cover')
    .neq('id', spreadId);
  if (demoteError) throw new Error(`markSpreadAsCover (demote): ${demoteError.message}`);

  // position 0 puts the Cover first — it opens the book in the Reading Flow (issue 06) —
  // regardless of which Spread the admin promoted.
  const { error } = await supabase
    .from('spreads')
    .update({ kind: 'cover', page_start: null, page_end: null, position: 0 })
    .eq('id', spreadId);
  if (error) throw new Error(`markSpreadAsCover: ${error.message}`);

  // Back cover_url with the Spread's first Photo.
  const photos = await getSpreadPhotos(spreadId);
  const source = photos.find((p) => p.photo_url)?.photo_url;
  if (!source) return;

  await setBookCoverArtwork(bookId, source);
}

// Copies a page-photos image into the public book-covers bucket and points books.cover_url at
// it. Shared by every path that changes a Book's artwork (designating a Cover, replacing the
// Cover Photo's image).
//
// Uses the storage server-side copy (destinationBucket) rather than download+upload: React
// Native's Blob can't be constructed from an ArrayBuffer, so pulling the bytes through the
// device fails ("Creating blobs from 'ArrayBuffer' ... are not supported"). This never leaves
// the server.
async function setBookCoverArtwork(bookId: string, sourcePath: string): Promise<void> {
  // Write each new cover to a *unique* path. Cover URLs are public and long-lived, so reusing
  // one path (e.g. `${bookId}.jpg`) leaves the CDN and the app's image cache serving the old
  // artwork after a cover change — the URL never changes, so nothing invalidates. A fresh path
  // changes the URL, which every render site picks up for free.
  const { data: existing } = await supabase
    .from('books')
    .select('cover_url')
    .eq('id', bookId)
    .single();
  const previousCoverPath = existing?.cover_url as string | null | undefined;

  const coverPath = `${bookId}-${Date.now()}.jpg`;
  const { error: copyError } = await supabase.storage
    .from('page-photos')
    .copy(sourcePath, coverPath, { destinationBucket: 'book-covers' });
  if (copyError) throw new Error(`setBookCoverArtwork (copy): ${copyError.message}`);

  const { error: bookError } = await supabase
    .from('books')
    .update({ cover_url: coverPath })
    .eq('id', bookId);
  if (bookError) throw new Error(`setBookCoverArtwork (book): ${bookError.message}`);

  // Drop the superseded artwork so the bucket doesn't accumulate orphans.
  if (previousCoverPath && previousCoverPath !== coverPath) {
    await supabase.storage.from('book-covers').remove([previousCoverPath]);
  }
}

// ---------------------------------------------------------------------------
// Phrase reorder / merge / split
// ---------------------------------------------------------------------------

// Reorders a Spread's placements. Positions are spread-scoped, so a phrase can be moved
// anywhere in the Spread — including across Photo boundaries (issue 08).
export async function reorderDraftPhrases(
  spreadId: string,
  orderedIds: string[],
): Promise<void> {
  if (orderedIds.length === 0) return;
  const { error } = await supabase.rpc('reorder_spread_placements', {
    p_spread_id: spreadId,
    p_ordered_ids: orderedIds,
  });
  if (error) throw new Error(`reorderDraftPhrases: ${error.message}`);
}

// Compose the current Spread's Photos into the prior Spread (ADR 0028, issue 02c-ii). Replaces
// the old page merge: the source Spread's Photos move onto the target and the source Spread is
// deleted (no stub, no phrase movement).
//
// Because the source row is deleted, its Spread Landmark has to go somewhere: the RPC gives it to
// the target when the target has none, and otherwise discards it. A SQL function can't delete a
// storage object, so it returns the discarded path (null when nothing was discarded) and the
// cleanup happens here — keeping it in this wrapper rather than at the call site means no future
// caller can forget it, matching how setSpreadLandmark and clearSpreadLandmark own their own
// storage cleanup.
export async function combinePhotosIntoPrior(
  sourceSpreadId: string,
  targetSpreadId: string,
): Promise<void> {
  const { data, error } = await supabase.rpc('combine_photos_into_prior', {
    p_source_page_id: sourceSpreadId,
    p_target_page_id: targetSpreadId,
  });
  if (error) throw new Error(`combinePhotosIntoPrior: ${error.message}`);

  // Best-effort, like clearSpreadLandmark: the merge itself has already committed, so a failed
  // object delete is a leaked file, not a failed merge, and must not surface as one.
  const orphanedLandmark = data as string | null;
  if (orphanedLandmark) {
    await supabase.storage.from('spread-landmarks').remove([orphanedLandmark]);
  }
}

export async function mergeDraftPhrases(
  spreadId: string,
  phraseAId: string,
  phraseBId: string,
  merged: Pick<SpreadDraftPhrase, 'chinese' | 'pinyin' | 'definition' | 'assessment_level' | 'line_break_after'>,
): Promise<SpreadDraftPhrase> {
  const { data: rows, error: fetchError } = await supabase
    .from('draft_photo_phrases')
    .select('id, position, draft_phrase_id, photo_id, spread_id')
    .in('id', [phraseAId, phraseBId]);
  if (fetchError) throw new Error(`mergeDraftPhrases (fetch): ${fetchError.message}`);

  const rowA = rows!.find((r: any) => r.id === phraseAId)!;
  const rowB = rows!.find((r: any) => r.id === phraseBId)!;
  const lowerPos = Math.min(rowA.position, rowB.position);
  const higherPos = Math.max(rowA.position, rowB.position);
  // The merged phrase inherits the earlier placement's Photo for provenance; ordering is
  // spread-scoped, so the two may have come from different Photos (issue 08).
  const photoId = rowA.position <= rowB.position ? rowA.photo_id : rowB.photo_id;
  const sourceDraftPhraseIds: string[] = [rowA.draft_phrase_id, rowB.draft_phrase_id];

  // Delete both placements.
  const { error: delError } = await supabase
    .from('draft_photo_phrases')
    .delete()
    .in('id', [phraseAId, phraseBId]);
  if (delError) throw new Error(`mergeDraftPhrases (delete): ${delError.message}`);

  // Clean up any orphaned draft_phrases.
  for (const draftPhraseId of sourceDraftPhraseIds) {
    await deleteOrphanedDraftPhrase(draftPhraseId);
  }

  // Shift placements after the higher position down by 1.
  const { error: shiftError } = await supabase.rpc('shift_spread_placements', {
    p_spread_id: spreadId,
    p_above_position: higherPos,
    p_delta: -1,
  });
  if (shiftError) throw new Error(`mergeDraftPhrases (shift): ${shiftError.message}`);

  // Get book_id for the merged phrase lookup.
  const { data: pageRow, error: pageError } = await supabase
    .from('spreads')
    .select('book_id')
    .eq('id', spreadId)
    .single();
  if (pageError) throw new Error(`mergeDraftPhrases (spread): ${pageError.message}`);

  const mergedDraftPhraseId = await findOrCreateDraftPhrase(
    pageRow.book_id,
    merged.chinese,
    merged.pinyin,
    merged.definition,
    merged.assessment_level,
  );

  const { data, error: insertError } = await supabase
    .from('draft_photo_phrases')
    .insert({
      photo_id: photoId,
      spread_id: spreadId,
      draft_phrase_id: mergedDraftPhraseId,
      position: lowerPos,
      line_break_after: merged.line_break_after,
    })
    .select('*, draft_phrases(*)')
    .single();
  if (insertError) throw new Error(`mergeDraftPhrases (insert): ${insertError.message}`);
  return flattenPlacement(data);
}

export async function splitDraftPhrase(
  originalId: string,
  spreadId: string,
  fragments: Array<Pick<SpreadDraftPhrase, 'chinese' | 'pinyin' | 'definition' | 'line_break_after'>>,
): Promise<SpreadDraftPhrase[]> {
  if (fragments.length === 0) throw new Error('splitDraftPhrase: no fragments provided');

  const { data: original, error: fetchError } = await supabase
    .from('draft_photo_phrases')
    .select('position, draft_phrase_id, photo_id, spread_id')
    .eq('id', originalId)
    .single();
  if (fetchError) throw new Error(`splitDraftPhrase (fetch): ${fetchError.message}`);
  const originalPos = original.position;
  const originalDraftPhraseId = original.draft_phrase_id;
  const photoId = original.photo_id; // fragments inherit the original's provenance
  const extraSlots = fragments.length - 1;

  const { data: pageRow, error: pageError } = await supabase
    .from('spreads')
    .select('book_id')
    .eq('id', spreadId)
    .single();
  if (pageError) throw new Error(`splitDraftPhrase (spread): ${pageError.message}`);

  // Delete the original placement.
  const { error: delError } = await supabase
    .from('draft_photo_phrases')
    .delete()
    .eq('id', originalId);
  if (delError) throw new Error(`splitDraftPhrase (delete): ${delError.message}`);

  await deleteOrphanedDraftPhrase(originalDraftPhraseId);

  // Shift subsequent placements up by extraSlots.
  if (extraSlots > 0) {
    const { error: shiftError } = await supabase.rpc('shift_spread_placements', {
      p_spread_id: spreadId,
      p_above_position: originalPos,
      p_delta: extraSlots,
    });
    if (shiftError) throw new Error(`splitDraftPhrase (shift): ${shiftError.message}`);
  }

  // Insert each fragment: find-or-create its draft_phrase, then insert placement.
  const inserted: SpreadDraftPhrase[] = [];
  for (let i = 0; i < fragments.length; i++) {
    const f = fragments[i];
    const draftPhraseId = await findOrCreateDraftPhrase(
      pageRow.book_id,
      f.chinese,
      f.pinyin,
      f.definition,
      null,
    );
    const { data, error: insertError } = await supabase
      .from('draft_photo_phrases')
      .insert({
        photo_id: photoId,
        spread_id: spreadId,
        draft_phrase_id: draftPhraseId,
        position: originalPos + i,
        line_break_after: f.line_break_after,
      })
      .select('*, draft_phrases(*)')
      .single();
    if (insertError) throw new Error(`splitDraftPhrase (insert): ${insertError.message}`);
    inserted.push(flattenPlacement(data));
  }

  return inserted;
}

export async function getDraftPhraseDefinition(
  chinese: string,
  pinyin: string
): Promise<string | null> {
  const { data } = await supabase
    .from('phrases')
    .select('definition')
    .eq('chinese', chinese)
    .eq('pinyin', pinyin)
    .maybeSingle();
  return data?.definition ?? null;
}
